import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * The typographic device the whole dashboard hangs off: an uppercase eyebrow,
 * a monospaced tabular figure, and a quiet caption. Hierarchy comes entirely
 * from scale and weight — the figure is never coloured.
 */
export function Figure({
  label,
  value,
  unit,
  caption,
  size = 'md',
  className,
}: {
  label: string;
  value: string;
  unit?: string;
  caption?: ReactNode;
  /** `hero` is reserved for the single dominant number on a screen. */
  size?: 'sm' | 'md' | 'hero';
  className?: string;
}) {
  // A mono figure has no break opportunity — "123 456,78" is joined by a
  // non-breaking space — so below 360px the largest steps are stepped down one
  // notch rather than allowed to push the page sideways. They are still by a
  // wide margin the biggest thing on that screen.
  const valueClass = {
    sm: 'text-md font-medium',
    md: 'text-2xl font-medium max-[359px]:text-xl',
    hero: 'text-3xl font-medium max-[359px]:text-2xl',
  }[size];

  return (
    <div className={cn('min-w-0', className)}>
      <p className="eyebrow">{label}</p>
      <p
        className={cn(
          'num mt-1.5 tracking-tight text-fg',
          size === 'hero' && 'mt-3',
          valueClass,
        )}
      >
        {value}
        {unit ? (
          <span className="ml-1.5 text-md font-normal text-fg-muted">{unit}</span>
        ) : null}
      </p>
      {caption ? <div className="mt-2 text-sm text-fg-secondary">{caption}</div> : null}
    </div>
  );
}

/**
 * One cell of the compact stat strip. Values stay mono so the row reads as a
 * set of numbers rather than a row of cards.
 */
export function Stat({
  label,
  value,
  tone = 'neutral',
  href,
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'warn' | 'danger';
  href?: ReactNode;
}) {
  return (
    <div className="min-w-0 px-4 py-3.5">
      {/* Wraps rather than truncates: «Ожидают подтверждения» is wider than
          "Awaiting confirmation" and would clip in a four-column strip on a
          1024px screen. A two-line label is readable; a cut-off one is not. */}
      <p className="eyebrow leading-snug">{label}</p>
      <p
        className={cn(
          'num mt-1 text-lg font-medium tracking-tight',
          tone === 'neutral' && 'text-fg',
          tone === 'warn' && 'text-warn-700',
          tone === 'danger' && 'text-danger-700',
        )}
      >
        {value}
      </p>
      {href ? <div className="mt-1 text-xs">{href}</div> : null}
    </div>
  );
}
